import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, X, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  createTestSurvey,
  updateTestSurvey,
  fetchTestSurvey,
  simulateTestSurvey,
  type TestSurveyQualification,
  type TestSurveyQuota,
  type TestSurveyPayload,
} from '../../lib/adminApi';

const CATEGORIES = ['Technology', 'Healthcare', 'Finance', 'CPG/Food', 'Auto', 'Entertainment', 'Travel', 'Retail', 'Other'];

const QUALIFICATION_ATTRIBUTES: Record<string, { label: string; type: 'range' | 'checkbox' | 'multiselect' | 'boolean'; options?: string[] }> = {
  age: { label: 'Age', type: 'range' },
  gender: { label: 'Gender', type: 'checkbox', options: ['Male', 'Female', 'Non-binary'] },
  income_band: { label: 'Household Income', type: 'checkbox', options: ['Under $25k', '$25k-$50k', '$50k-$75k', '$75k-$100k', '$100k-$150k', '$150k+'] },
  education: { label: 'Education', type: 'checkbox', options: ['High School', 'Some College', "Bachelor's", "Master's", 'Doctorate+'] },
  employment: { label: 'Employment', type: 'checkbox', options: ['Full-time', 'Part-time', 'Self-employed', 'Retired', 'Student', 'Unemployed'] },
  has_children: { label: 'Has Children', type: 'boolean' },
  marital_status: { label: 'Marital Status', type: 'checkbox', options: ['Single', 'Married', 'Divorced', 'Widowed', 'Separated'] },
  state: { label: 'State', type: 'multiselect', options: ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'] },
  zip_code: { label: 'ZIP Code', type: 'multiselect' },
  home_ownership: { label: 'Home Ownership', type: 'checkbox', options: ['Own', 'Rent', 'Other'] },
  pet_ownership: { label: 'Pet Ownership', type: 'boolean' },
  vehicle_ownership: { label: 'Vehicle Ownership', type: 'boolean' },
};

type Tab = 'basics' | 'qualifications' | 'quotas' | 'urls';

export default function AdminSurveyBuilder() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('basics');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<'active' | 'paused' | 'draft'>('active');
  const [cpi, setCpi] = useState(2);
  const [loi, setLoi] = useState(10);
  const [ir, setIr] = useState(100);
  const [completesTarget, setCompletesTarget] = useState(999999);
  const [fieldEndDate, setFieldEndDate] = useState('');
  const [qualifications, setQualifications] = useState<TestSurveyQualification[]>([]);
  const [quotas, setQuotas] = useState<TestSurveyQuota[]>([]);
  const [showAddQual, setShowAddQual] = useState(false);

  // Load existing survey for edit
  const { data: existingData } = useQuery({
    queryKey: ['admin', 'surveys', 'test', id],
    queryFn: () => fetchTestSurvey(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existingData?.survey) {
      const s = existingData.survey;
      setTitle(s.title);
      setCategory(s.category ?? '');
      setStatus(s.status);
      setLoi(s.estimated_length_minutes);
      const meta = s.metadata as Record<string, unknown> ?? {};
      setCpi((meta.cpi as number) ?? 2);
      setIr((meta.ir as number) ?? 100);
      setCompletesTarget((meta.completesTarget as number) ?? 999999);
      setFieldEndDate((meta.fieldEndDate as string) ?? '');
      setQualifications((meta.qualifications as TestSurveyQualification[]) ?? []);
      setQuotas((meta.quotas as TestSurveyQuota[]) ?? []);
    }
  }, [existingData]);

  const saveMutation = useMutation({
    mutationFn: (payload: TestSurveyPayload) =>
      isEdit ? updateTestSurvey(id!, payload) : createTestSurvey(payload),
    onSuccess: () => {
      setSuccess(true);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'surveys'] });
      setTimeout(() => navigate('/admin/surveys'), 1000);
    },
    onError: (err: Error) => setError(err.message),
  });

  const simMutation = useMutation({
    mutationFn: ({ outcome }: { outcome: 'complete' | 'screenout' | 'quotafull' | 'security-terminate' }) =>
      simulateTestSurvey(id!, outcome),
    onSuccess: (data: { redirectUrl: string }) => {
      window.location.href = data.redirectUrl;
    },
  });

  function handleSave() {
    setError(null);
    saveMutation.mutate({
      title, category: category || undefined, status, cpi, loi, ir,
      completesTarget, fieldEndDate: fieldEndDate || undefined,
      qualifications, quotas,
    });
  }

  function addQualification(attribute: string) {
    const config = QUALIFICATION_ATTRIBUTES[attribute];
    if (!config) return;
    setQualifications((prev) => [...prev, {
      attribute,
      operator: config.type === 'range' ? 'gte' : 'in',
      values: [],
      isPrescreen: false,
    }]);
    setShowAddQual(false);
  }

  function removeQualification(index: number) {
    setQualifications((prev) => prev.filter((_, i) => i !== index));
  }

  function updateQualValues(index: number, values: string[]) {
    setQualifications((prev) => prev.map((q, i) => i === index ? { ...q, values } : q));
  }

  function togglePrescreen(index: number) {
    setQualifications((prev) => prev.map((q, i) => i === index ? { ...q, isPrescreen: !q.isPrescreen, prescreenQuestion: q.isPrescreen ? undefined : `Do you ${QUALIFICATION_ATTRIBUTES[q.attribute]?.label.toLowerCase()}?` } : q));
  }

  const TABS: { key: Tab; label: string }[] = [
    { key: 'basics', label: 'Basics' },
    { key: 'qualifications', label: 'Qualifications' },
    { key: 'quotas', label: 'Quotas' },
    { key: 'urls', label: 'URLs & Simulate' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/admin/surveys')} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold font-display text-white">
          {isEdit ? 'Edit Test Survey' : 'Create Test Survey'}
        </h1>
      </div>

      {error && (
        <div className="bg-red-900/30 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div className="bg-green-900/30 border border-green-700 rounded-lg px-4 py-3 text-sm text-green-300 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> Survey saved successfully!
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 bg-slate-800 rounded-lg p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${tab === t.key ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-slate-800 rounded-xl p-6">
        {/* TAB 1: Basics */}
        {tab === 'basics' && (
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" placeholder="Enter survey title" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Category</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500">
                  <option value="">Select category</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Status</label>
                <div className="flex gap-2">
                  {(['active', 'paused', 'draft'] as const).map((s) => (
                    <button key={s} onClick={() => setStatus(s)} className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${status === s ? (s === 'active' ? 'bg-green-900/50 text-green-400 border border-green-700' : s === 'paused' ? 'bg-amber-900/50 text-amber-400 border border-amber-700' : 'bg-slate-700 text-slate-300 border border-slate-600') : 'bg-slate-900 text-slate-500 border border-slate-700'}`}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">CPI ($)</label>
                <input type="number" step="0.01" value={cpi} onChange={(e) => setCpi(parseFloat(e.target.value) || 0)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">LOI (min)</label>
                <input type="number" value={loi} onChange={(e) => setLoi(parseInt(e.target.value) || 10)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">IR %</label>
                <input type="number" value={ir} onChange={(e) => setIr(parseInt(e.target.value) || 100)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Completes</label>
                <input type="number" value={completesTarget} onChange={(e) => setCompletesTarget(parseInt(e.target.value) || 999999)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Field End Date (optional)</label>
              <input type="date" value={fieldEndDate} onChange={(e) => setFieldEndDate(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500" />
            </div>
          </div>
        )}

        {/* TAB 2: Qualifications */}
        {tab === 'qualifications' && (
          <div className="space-y-4">
            {qualifications.length === 0 && !showAddQual && (
              <div className="text-center py-12 text-slate-500">
                <p className="text-lg font-medium mb-2">No Questions</p>
                <p className="text-sm mb-4">Add qualification questions to target specific demographics</p>
              </div>
            )}

            {qualifications.map((qual, idx) => {
              const config = QUALIFICATION_ATTRIBUTES[qual.attribute];
              return (
                <div key={idx} className="bg-slate-900 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-white">{config?.label ?? qual.attribute}</h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => togglePrescreen(idx)}
                        className={`text-xs px-2 py-1 rounded ${qual.isPrescreen ? 'bg-indigo-900/50 text-indigo-300 border border-indigo-700' : 'bg-slate-800 text-slate-500 border border-slate-700'}`}
                      >
                        {qual.isPrescreen ? 'Prescreen ON' : 'Prescreen'}
                      </button>
                      <button onClick={() => removeQualification(idx)} className="text-red-400 hover:text-red-300">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {config?.type === 'checkbox' && config.options && (
                    <div className="flex flex-wrap gap-2">
                      {config.options.map((opt) => (
                        <button
                          key={opt}
                          onClick={() => {
                            const vals = qual.values.includes(opt)
                              ? qual.values.filter((v) => v !== opt)
                              : [...qual.values, opt];
                            updateQualValues(idx, vals);
                          }}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${qual.values.includes(opt) ? 'bg-indigo-900/50 text-indigo-300 border-indigo-700' : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-500'}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}

                  {config?.type === 'boolean' && (
                    <div className="flex gap-2">
                      {['Yes', 'No'].map((opt) => (
                        <button
                          key={opt}
                          onClick={() => updateQualValues(idx, [opt.toLowerCase()])}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${qual.values.includes(opt.toLowerCase()) ? 'bg-indigo-900/50 text-indigo-300 border-indigo-700' : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-500'}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}

                  {config?.type === 'range' && (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={qual.values[0] ?? ''}
                        onChange={(e) => updateQualValues(idx, [e.target.value, qual.values[1] ?? ''])}
                        className="w-20 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-sm"
                      />
                      <span className="text-slate-500">to</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={qual.values[1] ?? ''}
                        onChange={(e) => updateQualValues(idx, [qual.values[0] ?? '', e.target.value])}
                        className="w-20 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-sm"
                      />
                    </div>
                  )}

                  {config?.type === 'multiselect' && config.options && (
                    <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
                      {config.options.map((opt) => (
                        <button
                          key={opt}
                          onClick={() => {
                            const vals = qual.values.includes(opt)
                              ? qual.values.filter((v) => v !== opt)
                              : [...qual.values, opt];
                            updateQualValues(idx, vals);
                          }}
                          className={`text-xs px-2 py-1 rounded border transition-colors ${qual.values.includes(opt) ? 'bg-indigo-900/50 text-indigo-300 border-indigo-700' : 'bg-slate-800 text-slate-400 border-slate-700'}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}

                  {config?.type === 'multiselect' && !config.options && (
                    <input
                      placeholder="Comma-separated values (e.g. 10001, 10002)"
                      value={qual.values.join(', ')}
                      onChange={(e) => updateQualValues(idx, e.target.value.split(',').map((v) => v.trim()).filter(Boolean))}
                      className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm"
                    />
                  )}

                  {qual.isPrescreen && (
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Prescreen Question Text</label>
                      <input
                        value={qual.prescreenQuestion ?? ''}
                        onChange={(e) => setQualifications((prev) => prev.map((q, i) => i === idx ? { ...q, prescreenQuestion: e.target.value } : q))}
                        className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm"
                      />
                    </div>
                  )}

                  <div className="text-xs text-slate-500">
                    Selected: {qual.values.length > 0 ? qual.values.join(', ') : 'None'}
                  </div>
                </div>
              );
            })}

            {showAddQual && (
              <div className="bg-slate-900 rounded-lg p-4">
                <h3 className="text-sm font-medium text-white mb-3">Choose Qualification Type</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(QUALIFICATION_ATTRIBUTES)
                    .filter(([key]) => !qualifications.some((q) => q.attribute === key))
                    .map(([key, config]) => (
                      <button
                        key={key}
                        onClick={() => addQualification(key)}
                        className="text-left bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-300 transition-colors"
                      >
                        {config.label}
                      </button>
                    ))}
                </div>
                <button onClick={() => setShowAddQual(false)} className="mt-3 text-xs text-slate-500 hover:text-slate-300">Cancel</button>
              </div>
            )}

            <button
              onClick={() => setShowAddQual(true)}
              className="inline-flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 font-medium"
            >
              <Plus className="w-4 h-4" /> Add Question
            </button>
          </div>
        )}

        {/* TAB 3: Quotas */}
        {tab === 'quotas' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-400">Set target completes per answer option for any qualification.</p>
            {qualifications.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Add qualifications first to set quotas.</p>
            ) : (
              qualifications.map((qual, qIdx) => {
                const existingQuota = quotas.find((q) => q.attribute === qual.attribute);
                if (qual.values.length === 0) return null;
                return (
                  <div key={qIdx} className="bg-slate-900 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-white mb-3">{QUALIFICATION_ATTRIBUTES[qual.attribute]?.label ?? qual.attribute}</h3>
                    <div className="space-y-2">
                      {qual.values.map((val) => {
                        const existing = existingQuota?.values.find((v) => v.answer === val);
                        return (
                          <div key={val} className="flex items-center gap-3">
                            <span className="text-sm text-slate-300 w-32 truncate">{val}</span>
                            <input
                              type="number"
                              placeholder="Target"
                              value={existing?.target ?? ''}
                              onChange={(e) => {
                                const target = parseInt(e.target.value) || 0;
                                setQuotas((prev) => {
                                  const idx = prev.findIndex((q) => q.attribute === qual.attribute);
                                  if (idx === -1) {
                                    return [...prev, { attribute: qual.attribute, values: [{ answer: val, target }] }];
                                  }
                                  const updated = [...prev];
                                  const valIdx = updated[idx].values.findIndex((v) => v.answer === val);
                                  if (valIdx === -1) {
                                    updated[idx] = { ...updated[idx], values: [...updated[idx].values, { answer: val, target }] };
                                  } else {
                                    updated[idx] = { ...updated[idx], values: updated[idx].values.map((v, i) => i === valIdx ? { ...v, target } : v) };
                                  }
                                  return updated;
                                });
                              }}
                              className="w-24 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white text-sm"
                            />
                            <span className="text-xs text-slate-500">completes</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* TAB 4: URLs & Simulate */}
        {tab === 'urls' && (
          <div className="space-y-6">
            {isEdit && (
              <>
                <div>
                  <h3 className="text-sm font-medium text-slate-300 mb-2">Survey Entry URL</h3>
                  <div className="bg-slate-900 rounded-lg px-4 py-3 text-sm text-slate-400 font-mono break-all">
                    https://www.swipeopinion.com/survey/test/{id}?member_id=&#123;memberId&#125;
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-slate-300 mb-2">Redirect URLs (Reference)</h3>
                  <div className="space-y-2 text-sm font-mono">
                    {[
                      { label: 'Complete', url: 'https://api.swipeopinion.com/api/survey/complete' },
                      { label: 'Screenout', url: 'https://api.swipeopinion.com/api/survey/screenout' },
                      { label: 'Quota Full', url: 'https://api.swipeopinion.com/api/survey/quotafull' },
                      { label: 'Quality Term', url: 'https://api.swipeopinion.com/api/survey/security-terminate' },
                    ].map((r) => (
                      <div key={r.label} className="flex items-start gap-3">
                        <span className="text-slate-500 w-24 flex-shrink-0">{r.label}:</span>
                        <span className="text-slate-400 break-all">{r.url}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-slate-300 mb-3">Simulate Outcome</h3>
                  <p className="text-xs text-slate-500 mb-3">Test the full flow for YOUR account:</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => simMutation.mutate({ outcome: 'complete' })}
                      disabled={simMutation.isPending}
                      className="py-3 rounded-lg bg-green-900/30 border border-green-700 text-green-400 text-sm font-medium hover:bg-green-900/50 transition-colors disabled:opacity-50"
                    >
                      {'\u2713'} Simulate Complete
                    </button>
                    <button
                      onClick={() => simMutation.mutate({ outcome: 'screenout' })}
                      disabled={simMutation.isPending}
                      className="py-3 rounded-lg bg-amber-900/30 border border-amber-700 text-amber-400 text-sm font-medium hover:bg-amber-900/50 transition-colors disabled:opacity-50"
                    >
                      {'\u2717'} Simulate Screenout
                    </button>
                    <button
                      onClick={() => simMutation.mutate({ outcome: 'quotafull' })}
                      disabled={simMutation.isPending}
                      className="py-3 rounded-lg bg-blue-900/30 border border-blue-700 text-blue-400 text-sm font-medium hover:bg-blue-900/50 transition-colors disabled:opacity-50"
                    >
                      {'\u25CB'} Simulate Quota Full
                    </button>
                    <button
                      onClick={() => simMutation.mutate({ outcome: 'security-terminate' })}
                      disabled={simMutation.isPending}
                      className="py-3 rounded-lg bg-slate-700/50 border border-slate-600 text-slate-400 text-sm font-medium hover:bg-slate-700 transition-colors disabled:opacity-50"
                    >
                      {'\u2298'} Simulate Terminate
                    </button>
                  </div>
                </div>
              </>
            )}

            {!isEdit && (
              <p className="text-sm text-slate-500 text-center py-8">Save the survey first to access URLs and simulation.</p>
            )}
          </div>
        )}
      </div>

      {/* Save button */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate('/admin/surveys')} className="text-sm text-slate-400 hover:text-white">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saveMutation.isPending || !title}
          className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-6 py-2.5 rounded-lg transition-colors disabled:opacity-50"
        >
          {saveMutation.isPending ? 'Saving...' : isEdit ? 'Update Survey' : 'Create Survey'}
        </button>
      </div>
    </div>
  );
}
